import { useMemo, useState } from "react";
import SoftBox from "components/SoftBox";
import DataTable from "cxfcomponents/Tables/DataTable";
import SoftButton from "components/SoftButton";
import Icon from "@mui/material/Icon";
import { useConnectorVersions } from "hooks/useConnectorVersions";
import SoftTypography from "components/SoftTypography";
import moment from "moment";
import { VersionsMenu } from "./menu";
import { NewVersionDialog } from "./newVersionDialog";
import { DeleteVersionDialog } from "./deleteVersionDialog";
import { useResourceListener } from "hooks/useResourceListener";

export const Versions = ({ connector, isReadOnlyAccess }) => {
   const { connectorVersions, handleAppendVersion, handleRemoveVersion, handleUpdateVersion, refetchConnectorVersions } = useConnectorVersions(connector.id);
   const [showAddNewVersionDialog, setShowAddNewVersionDialog] = useState(false);
   const [showDeleteVersionDialog, setShowDeleteVersionDialog] = useState(false);
   const [versionToDelete, setVersionToDelete] = useState(null);
   useResourceListener("connectorsVersionUpdate", refetchConnectorVersions);

   const getHighestVersion = () => {
      if (connectorVersions.length === 0) return 1;

      return Math.max(...connectorVersions.map((cVersion) => parseFloat(cVersion.version))) + 1;
   };

   const getHighestSubVersion = (versionNumber) => {
      const versions = connectorVersions.filter((version) => version.version[0] === versionNumber);

      return Math.max(...versions.map((version) => parseFloat(version.version)));
   };

   const formatFileSize = (size) => {
      if (size === 0 || size == null) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
      const i = Math.floor(Math.log(size) / Math.log(k));
      return parseFloat((size / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
   };

   const tableData = useMemo(() => {
      return {
         columns: [
            {
               Header: "version",
               accessor: "version",
               disableSortBy: false,
               defaultSortOrder: "desc",
               canGroupBy: false,
               Cell: ({ value }) => (
                  <SoftBox
                     sx={{
                        backgroundColor: "#E4E4E4",
                        borderRadius: "2px",
                        color: "#6E7381",
                        fontWeight: 700,
                        marginLeft: "24px",
                        padding: "0 8px"
                     }}
                  >
                     {value}
                  </SoftBox>
               )
            },
            {
               Header: "file name",
               accessor: "fileName",
               width: 200,
               canGroupBy: false,
               Cell: ({ value }) => <SoftBox>{value}</SoftBox>
            },
            {
               Header: "file size",
               accessor: "fileSize",
               width: 200,
               canGroupBy: false,
               Cell: ({ value }) => (
                  <SoftBox
                     sx={{
                        width: "60px"
                     }}
                  >
                     {formatFileSize(value)}
                  </SoftBox>
               )
            },
            {
               Header: "date modified",
               accessor: "dateModified",
               width: 200,
               canGroupBy: false,
               Cell: ({ value }) => (
                  <SoftTypography
                     sx={{
                        fontSize: "12px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "start",
                        fontWeight: "500"
                     }}
                  >
                     <span>{value && moment(value).format("MM/DD/YYYY")}</span>
                     <span>{value && moment(value).format("h:mm:ss A")}</span>
                  </SoftTypography>
               )
            },
            {
               Header: "actions",
               accessor: "actions",
               canGroupBy: false,
               disableSortBy: true
            },
            {
               accessor: "id",
               show: false
            }
         ].map((column) => ({ ...column, disableResize: true })),
         rows:
            connectorVersions?.map((version) => ({
               version: version.version,
               fileName: version.file_name,
               fileSize: version.file_size,
               dateModified: version.updated_datetime || version.created_datetime,
               actions: (
                  <VersionsMenu
                     version={version}
                     setVersionToDelete={() => {
                        setVersionToDelete(version);
                        setShowDeleteVersionDialog(true);
                     }}
                     handleUpdateVersion={handleUpdateVersion}
                     isReadOnlyAccess={isReadOnlyAccess}
                  />
               )
            })) ?? []
      };
   }, [connectorVersions]);

   return (
      <>
         <SoftBox>
            <SoftBox
               sx={{
                  maxWidth: "max-content",
                  margin: "1rem 0",
                  marginLeft: "auto",
                  paddingRight: "3rem"
               }}
            >
               {!isReadOnlyAccess && (
                  <SoftButton
                     sx={{
                        display: "flex",
                        gap: "4px",
                        marginLeft: "auto !important"
                     }}
                     variant="outlined"
                     color="info"
                     onClick={() => setShowAddNewVersionDialog(true)}
                  >
                     <Icon>add</Icon> new version
                  </SoftButton>
               )}
            </SoftBox>
            <DataTable table={tableData} entriesPerPage={{ defaultValue: 25 }} hiddenTopButtons={true} noTopPart tableHeight="clamp(350px, calc(100vh - 130px), calc(100vh - 500px))" />
         </SoftBox>
         <NewVersionDialog
            openNewVersionDialog={showAddNewVersionDialog}
            onClose={() => setShowAddNewVersionDialog(false)}
            handleAppendVersion={handleAppendVersion}
            handleUpdateVersion={handleUpdateVersion}
            connector={connector}
            connectorVersions={connectorVersions}
            getHighestSubVersion={getHighestSubVersion}
            getHighestVersion={getHighestVersion}
         />
         <DeleteVersionDialog
            openDeleteVersionDialog={showDeleteVersionDialog}
            onClose={() => {
               setShowDeleteVersionDialog(false);
               setVersionToDelete(null);
            }}
            versionToDelete={versionToDelete}
            onConfirmDelete={() => {
               handleRemoveVersion(versionToDelete);
               setShowDeleteVersionDialog(false);
               setVersionToDelete(null);
            }}
            connector={connector}
         />
      </>
   );
};
